import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calendar, TrendingDown, TrendingUp, DollarSign } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import NationalitySelect from "../components/NationalitySelect";
import { getNationalityCurrency } from "../data/nationalities";
import { useCurrency } from "../context/CurrencyContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VisaCalendarPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [destination, setDestination] = useState(searchParams.get("destination") || "");
  const [nationality, setNationality] = useState(searchParams.get("nationality") || "");
  const [countries, setCountries] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { convert, currency, setCurrency } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/countries`).then(r => setCountries(r.data));
  }, []);

  const handleSearch = async () => {
    if (!destination || !nationality) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/visa-calendar`, { params: { destination, nationality } });
      setData(d);
    } catch {} finally { setLoading(false); }
  };

  const getBarColor = (level) => {
    if (level === "peak") return "bg-red-400";
    if (level === "low") return "bg-emerald-400";
    return "bg-blue-400";
  };

  const maxPrice = data ? Math.max(...data.months.map(m => m.standard_price)) : 0;
  const minPrice = data ? Math.min(...data.months.map(m => m.standard_price)) : 0;

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Home</button>

        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4 border border-blue-100"><Calendar className="w-7 h-7 text-blue-600" /></div>
          <h1 className="heading-font text-3xl font-bold text-slate-900 mb-2">Visa Cost Calendar</h1>
          <p className="text-sm text-slate-500">Find the cheapest month to apply. Prices vary by travel season.</p>
        </div>

        {/* Search */}
        <div className="card-elevated p-6 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div><label className="text-xs text-slate-500 mb-1 block">Nationality</label>
              <NationalitySelect value={nationality} onValueChange={(v) => { setNationality(v); setCurrency(getNationalityCurrency(v)); }} /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Destination</label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger className="w-full h-11 border-slate-200 rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 max-h-60">
                  {countries.map(c => <SelectItem key={c.country_code} value={c.country_code}><span className="flex items-center gap-2">{c.flag} {c.country_name}</span></SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Currency</label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="w-full h-11 border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {["USD", "INR", "GBP", "EUR", "AED", "CAD", "AUD"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="flex items-end">
              <button onClick={handleSearch} disabled={loading || !destination || !nationality} data-testid="calendar-search"
                className="w-full h-11 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-40">
                {loading ? "Loading..." : "Show Calendar"}
              </button>
            </div>
          </div>
        </div>

        {data && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="calendar-results">
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="card-elevated p-5 border-l-4 border-l-emerald-400">
                <div className="flex items-center gap-2 mb-1"><TrendingDown className="w-4 h-4 text-emerald-600" /><span className="text-xs text-slate-400">Cheapest Month</span></div>
                <p className="heading-font text-xl font-bold text-slate-900">{data.cheapest_month}</p>
                <p className="text-sm text-emerald-600 font-semibold">{convert(data.cheapest_price)}</p>
              </div>
              <div className="card-elevated p-5 border-l-4 border-l-red-400">
                <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-red-500" /><span className="text-xs text-slate-400">Most Expensive</span></div>
                <p className="heading-font text-xl font-bold text-slate-900">{data.peak_month}</p>
                <p className="text-sm text-red-500 font-semibold">{convert(data.peak_price)}</p>
              </div>
              <div className="card-elevated p-5 border-l-4 border-l-blue-400">
                <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-blue-600" /><span className="text-xs text-slate-400">Potential Savings</span></div>
                <p className="heading-font text-xl font-bold text-blue-600">{convert(data.potential_savings)}</p>
                <p className="text-xs text-slate-400">by choosing the right month</p>
              </div>
            </div>

            {/* Bar chart */}
            <div className="card-elevated p-6 mb-8">
              <h3 className="heading-font text-lg font-semibold text-slate-900 mb-6">Price by Month (Standard Processing)</h3>
              <div className="flex items-end gap-2 h-48">
                {data.months.map((m) => {
                  const height = maxPrice > minPrice ? ((m.standard_price - minPrice) / (maxPrice - minPrice) * 70 + 30) : 50;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group">
                      <span className="text-[10px] text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">{convert(m.standard_price)}</span>
                      <motion.div initial={{ height: 0 }} animate={{ height: `${height}%` }} transition={{ duration: 0.5, delay: m.month * 0.05 }}
                        className={`w-full rounded-t-md ${getBarColor(m.level)} cursor-pointer relative group`}
                        title={`${m.name}: ${convert(m.standard_price)}`}>
                        {m.savings > 0 && (
                          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-emerald-600 font-bold whitespace-nowrap hidden group-hover:block">
                            Save {convert(m.savings)}
                          </span>
                        )}
                      </motion.div>
                      <span className={`text-[10px] font-medium ${m.is_peak ? "text-red-500" : "text-slate-500"}`}>{m.name}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-emerald-400" /><span className="text-[10px] text-slate-400">Low Season</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-blue-400" /><span className="text-[10px] text-slate-400">Normal</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-red-400" /><span className="text-[10px] text-slate-400">Peak Season</span></div>
              </div>
            </div>

            {/* Monthly grid */}
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {data.months.map(m => (
                <div key={m.month} className={`card-elevated p-3 text-center cursor-pointer ${m.name === data.cheapest_month ? "ring-2 ring-emerald-400" : ""}`}
                  onClick={() => navigate(`/pricing?destination=${destination}&nationality=${nationality}`)}>
                  <p className={`text-xs font-semibold ${m.is_peak ? "text-red-500" : "text-slate-600"}`}>{m.name}</p>
                  <p className="heading-font text-lg font-bold text-slate-900 mt-1">{convert(m.standard_price)}</p>
                  {m.savings > 0 && <p className="text-[10px] text-emerald-600 mt-0.5">Save {convert(m.savings)}</p>}
                  {m.is_peak && <p className="text-[10px] text-red-400 mt-0.5">Peak</p>}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
